import { inject } from '@angular/core';
import { type CanActivateFn } from '@angular/router';
import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { AccountService } from '@frontendlib/common/services/account.service';
import { storageAccountId } from '@frontendlib/common/utils/storage-account';
import { catchError, map, type Observable, of, take, timeout } from 'rxjs';

/** The same bound as `teamSpaceBootGuard`'s: a healthy stack answers the one cached account read long before it. */
const ACCOUNT_KIND_READY_TIMEOUT_MS = 10_000;

/**
 * No signed-in window opens while the login's kind is still unknown.
 *
 * A session stored before accounts sent the kind reads as `null` until the account read answers, and a
 * component that reads the kind once while it builds would decide "personal" for a team login. This
 * holds the route for that one read — the same `GET /api/account` the boot guard makes, shared, never a
 * second request — and `AccountService` reconciles the kind before it publishes the account, so every
 * window below sees the settled kind.
 *
 * It lets the window through at once when the kind is known or nobody is signed in, and, after the
 * bound or a failed read, with the kind still `null`: unknown means hidden, the product as it was
 * before team workspaces.
 */
export const accountKindReadyGuard: CanActivateFn = (): boolean | Observable<boolean> => {
  if (inject(AccountKindService).kind() !== null || storageAccountId() === null) {
    return true;
  }
  return inject(AccountService)
    .getAccount()
    .pipe(
      take(1),
      timeout(ACCOUNT_KIND_READY_TIMEOUT_MS),
      map((): boolean => true),
      catchError((): Observable<boolean> => of(true)),
    );
};
